import { ApiService, TApiServiceError, TApiServiceOptions } from "../apiService";
import type {
  TAIChatLogResponse,
  TChatResponse,
  TCompanyReadinessResponse,
  TDashboardSummaryResponse,
  TGenerateAnalysisResponse,
  THealthCheckResponse,
  TInvestorChatResumeRequest,
  TInvestorChatStartRequest,
  TInvestorRecommendationResponse,
  TOnboardingSummaryRequest,
  TOnboardingSummaryResponse,
  TReadinessStatusResponse,
  TReevaluateReadinessRequest,
} from "./ai.types";

import { handleAsync } from "@/utils";
import { TApiResponse } from "../../types/api";
import { withAuthHooks } from "../auth/auth.ky.hooks";

const apiService = new ApiService({
  hooks: {
    ...withAuthHooks,
  },
});

class AIService {
  private rootEndpoint = "ai" as const;

  generateChatResponse(
    payload: {
      userMessage: string;
      threadContext: Record<string, unknown>;
      threadId?: string;
    },
    options?: TApiServiceOptions
  ) {
    return handleAsync<TApiServiceError, TApiResponse<TChatResponse>>(() =>
      apiService.post(
        `${this.rootEndpoint}/chat/response`,
        {
          user_message: payload.userMessage,
          thread_context: payload.threadContext,
          thread_id: payload.threadId ?? null,
        },
        options
      )
    );
  }

  generateDashboardSummary(payload: { dataContext: Record<string, unknown> }, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TDashboardSummaryResponse>>(() =>
      apiService.post(`${this.rootEndpoint}/dashboard/summary`, { data_context: payload.dataContext }, options)
    );
  }

  startInvestorChat(payload: TInvestorChatStartRequest, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TChatResponse>>(() =>
      apiService.post(
        `${this.rootEndpoint}/investor/chat/start`,
        {
          thread_id: payload.threadId,
          investor_company_id: payload.investorCompanyId,
          section: payload.section,
          user_input: payload.userInput,
          company_id: payload.companyId ?? null,
        },
        options
      )
    );
  }

  resumeInvestorChat(payload: TInvestorChatResumeRequest, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TChatResponse>>(() =>
      apiService.post(
        `${this.rootEndpoint}/investor/chat/resume`,
        {
          thread_id: payload.threadId,
          investor_company_id: payload.investorCompanyId,
          section: payload.section,
          user_feedback: payload.userFeedback,
          company_id: payload.companyId ?? null,
        },
        options
      )
    );
  }

  getChatLogs(limit = 50, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TAIChatLogResponse[]>>(() =>
      apiService.get(`${this.rootEndpoint}/chat/logs?limit=${limit}`, options)
    );
  }

  getThreadChatLogs(threadId: string, limit = 50, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TAIChatLogResponse[]>>(() =>
      apiService.get(`${this.rootEndpoint}/chat/logs/${threadId}?limit=${limit}`, options)
    );
  }

  generateCompanyOnboardingSummary(
    companyId: string,
    payload?: TOnboardingSummaryRequest,
    options?: TApiServiceOptions
  ) {
    const body: Record<string, unknown> = {};
    if (payload?.userInput) body.user_input = payload.userInput;
    if (payload?.section) body.section = payload.section;
    if (payload?.companyRole) body.company_role = payload.companyRole;
    if (payload?.runId) body.run_id = payload.runId;

    return handleAsync<TApiServiceError, TApiResponse<TOnboardingSummaryResponse>>(() =>
      apiService.post(`${this.rootEndpoint}/company/${companyId}/onboarding-summary`, body, options)
    );
  }

  uploadFileToKnowledgeBase(companyId: string, file: File, options?: TApiServiceOptions) {
    const formData = new FormData();
    formData.append("file", file);
    formData.append("company_id", companyId);

    return handleAsync<TApiServiceError, TApiResponse<Record<string, unknown>>>(() =>
      apiService.post(`${this.rootEndpoint}/upload`, formData, options)
    );
  }

  getCompanyReadiness(companyId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TCompanyReadinessResponse | null>>(() =>
      apiService.get(`${this.rootEndpoint}/companies/${companyId}/readiness`, options)
    );
  }

  generateCompanyReadiness(companyId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TGenerateAnalysisResponse>>(() =>
      apiService.post(`${this.rootEndpoint}/companies/${companyId}/readiness/generate`, undefined, options)
    );
  }

  reevaluateCompanyReadiness(companyId: string, payload?: TReevaluateReadinessRequest, options?: TApiServiceOptions) {
    const body: Record<string, unknown> = {};
    if (payload?.section) body.section = payload.section;
    if (payload?.runId) body.run_id = payload.runId;

    return handleAsync<TApiServiceError, TApiResponse<TGenerateAnalysisResponse>>(() =>
      apiService.post(`${this.rootEndpoint}/companies/${companyId}/readiness/reevaluate`, body, options)
    );
  }

  getInvestorRecommendations(companyId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TInvestorRecommendationResponse | null>>(() =>
      apiService.get(`${this.rootEndpoint}/companies/${companyId}/recommendations`, options)
    );
  }

  downloadCompanyReadinessReport(companyId: string, options?: TApiServiceOptions) {
    return handleAsync<
      TApiServiceError,
      {
        blob: Blob;
        filename: string | null;
      }
    >(async () => {
      const response = await apiService.getRaw(`${this.rootEndpoint}/companies/${companyId}/readiness/report`, options);
      const blob = await response.blob();
      const disposition = response.headers.get("content-disposition") ?? response.headers.get("Content-Disposition");
      let filename: string | null = null;
      if (disposition) {
        const filenameRegex = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i;
        const filenameMatch = filenameRegex.exec(disposition);
        if (filenameMatch?.[1]) {
          try {
            filename = decodeURIComponent(filenameMatch[1]);
          } catch {
            filename = filenameMatch[1];
          }
        }
      }
      return {
        blob,
        filename,
      };
    });
  }

  getReadinessStatus(companyId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TReadinessStatusResponse>>(() =>
      apiService.get(`${this.rootEndpoint}/companies/${companyId}/readiness/status`, options)
    );
  }

  getRecommendationsStatus(companyId: string, options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<TReadinessStatusResponse>>(() =>
      apiService.get(`${this.rootEndpoint}/companies/${companyId}/recommendations/status`, options)
    );
  }

  healthCheck(options?: TApiServiceOptions) {
    return handleAsync<TApiServiceError, TApiResponse<THealthCheckResponse>>(() =>
      apiService.get(`${this.rootEndpoint}/health`, options)
    );
  }
}

export const aiService = new AIService();
export type { TCompanyReadinessResponse, TGenerateAnalysisResponse, TInvestorRecommendationResponse };
